import { useMemo } from 'react';

import { DROPDOWN_FETCH_PAGE_SIZE } from '@klareospace/pharos-lib-front-utils';
import type { MultiSelectOption } from '@klareospace/pharos-lib-web-utils';

import { useSites } from './use-sites';

export interface UseSitesForSelectOptions {
  enabled?: boolean;
  excludeIds?: string[];
  ids?: string[];
  spaceIds?: string[];
}

export interface UseSitesForSelectReturn {
  /** True while the underlying sites query is in-flight. */
  isLoading: boolean;
  /** Resolved site options ready to pass to a select/multi-select component. */
  options: MultiSelectOption[];
}

export function useSitesForSelect(options?: UseSitesForSelectOptions): UseSitesForSelectReturn {
  const { enabled = true, excludeIds, ids, spaceIds } = options ?? {};

  // An explicit but empty allowlist means no site is selectable. Skip the query, since an
  // empty `ids` would otherwise be treated as "no filter" by the API and fetch every site.
  const hasEmptyAllowlist = ids !== undefined && ids.length === 0;

  const { data, isLoading } = useSites({
    enabled: enabled && !hasEmptyAllowlist,
    excludeIds,
    ids,
    page: 1,
    pageSize: DROPDOWN_FETCH_PAGE_SIZE,
    spaceIds,
  });

  const siteOptions = useMemo(
    () => (hasEmptyAllowlist ? [] : (data?.items ?? []).map((site) => ({ label: site.name, value: site.id }))),
    [data, hasEmptyAllowlist],
  );

  return { isLoading, options: siteOptions };
}
